import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuthStore } from '../../stores/authStore';
import { signOut } from '../../hooks/useAuth';
import { getSiteMode } from '../../lib/siteMode';

export default function Navbar() {
  const user = useAuthStore((s) => s.user);
  const profile = useAuthStore((s) => s.profile);
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const siteMode = getSiteMode();

  const explorerName = profile?.explorerName || profile?.displayName || '';

  async function handleSignOut() {
    await signOut();
    setMenuOpen(false);
    navigate('/', { replace: true });
  }

  return (
    <nav className="sticky top-0 z-50 border-b border-gray-200 bg-white/80 backdrop-blur-sm">
      <div className="mx-auto flex h-14 max-w-7xl items-center justify-between px-4">
        <Link to="/" className="text-lg font-bold text-brand-600">
          OMore
        </Link>

        {/* Desktop nav */}
        <div className="hidden items-center gap-6 md:flex">
          {user && profile?.hasCompletedSignUp && siteMode === 'kid' && (
            <>
              <Link
                to="/home"
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                Launchpad
              </Link>
              <Link
                to="/spotlight"
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                Spotlight
              </Link>
            </>
          )}

          {user ? (
            <>
              {siteMode === 'parent' && (
                <Link
                  to="/parent"
                  className="text-sm text-gray-600 hover:text-gray-900"
                >
                  Parent Portal
                </Link>
              )}
              <Link
                to="/account"
                title="Account Settings"
                className="max-w-[12rem] truncate text-sm font-semibold text-brand-600 transition-colors hover:text-brand-700"
              >
                {explorerName || 'Account'}
              </Link>
              <button
                onClick={handleSignOut}
                className="text-sm text-gray-500 transition-colors hover:text-gray-900"
              >
                Sign Out
              </button>
            </>
          ) : (
            <Link
              to="/auth"
              className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
            >
              Sign In
            </Link>
          )}
        </div>

        {/* Mobile hamburger */}
        <div className="md:hidden">
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="flex h-10 w-10 items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100"
            aria-label="Toggle menu"
          >
            {menuOpen ? (
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Mobile dropdown */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden border-t border-gray-100 bg-white md:hidden"
          >
            <div className="space-y-1 px-4 py-3">
              {user ? (
                <>
                  {profile?.hasCompletedSignUp && siteMode === 'kid' && (
                    <>
                      <Link
                        to="/home"
                        onClick={() => setMenuOpen(false)}
                        className="block rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        Launchpad
                      </Link>
                      <Link
                        to="/spotlight"
                        onClick={() => setMenuOpen(false)}
                        className="block rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        Spotlight
                      </Link>
                    </>
                  )}
                  {siteMode === 'parent' && (
                    <Link
                      to="/parent"
                      onClick={() => setMenuOpen(false)}
                      className="block rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      Parent Portal
                    </Link>
                  )}
                  <Link
                    to="/account"
                    onClick={() => setMenuOpen(false)}
                    className="block rounded-lg px-3 py-2 text-sm font-semibold text-brand-600 hover:bg-gray-50"
                  >
                    {explorerName || 'Account Settings'}
                  </Link>
                  <button
                    onClick={handleSignOut}
                    className="block w-full rounded-lg px-3 py-2 text-left text-sm text-red-600 hover:bg-gray-50"
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <Link
                  to="/auth"
                  onClick={() => setMenuOpen(false)}
                  className="block rounded-lg px-3 py-2 text-sm font-medium text-brand-600 hover:bg-gray-50"
                >
                  Sign In
                </Link>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
